import type { AgentDTO, SkillDTO, SkillLevel } from "../../shared/types";
import { shortId } from "../lib/scopes";
import { Icon, type IconName } from "./Icon";
import { Avatar, Badge } from "./ui";

/** Enough of a skill to say where it applies and who has it: the Skills page, My skills and the detail all pass one. */
export type Placed = Pick<SkillDTO, "level" | "userId" | "userName" | "scopeKey" | "scopeName" | "scopeTypeName" | "allAgents" | "grantedAgentIds">;

/**
 * The agents one person's own skills may be on: the ones that take people's skills, and the ones they own. The same
 * rule the platform holds, so every picker offers exactly what it would allow.
 */
export const agentsForPersonSkills = (agents: AgentDTO[], userId: string | null): AgentDTO[] =>
  userId ? agents.filter((a) => a.peopleCanAddSkills || a.ownerIds.includes(userId)) : [];

/**
 * The agents a person's skill is attached to but no longer reaches: the person has stopped owning the agent, or its
 * switch has been turned off. It is theirs to drop; until they do, no run of that agent is given the skill.
 */
export const inactiveAgentIds = (skill: Placed, agents: AgentDTO[]): string[] => {
  if (skill.level !== "person") return [];
  const allowed = agentsForPersonSkills(agents, skill.userId);
  return skill.grantedAgentIds.filter((id) => !allowed.some((a) => a.id === id));
};

/** Why an attachment is inactive, in the words of whoever is reading it. */
export const inactiveHint = (mine: boolean) => (mine ? "you no longer own this agent, so it is not given this skill" : "its person no longer owns this agent, so it is not given this skill");

/** A scope by its type and its name, as the Scopes page writes them: "Account · iSTORM", never an id on its own. */
export function scopeLabel(skill: Placed): string {
  const key = skill.scopeKey ?? "";
  const colon = key.indexOf(":");
  const type = skill.scopeTypeName || (colon < 0 ? "" : key.slice(0, colon)) || "Scope";
  const name = skill.scopeName || (colon < 0 ? "" : shortId(key.slice(colon + 1)));
  return name ? `${type} · ${name}` : type;
}

/** Where a skill applies, in three words: its person, its scope, or the agents'. */
export const levelLabel = (skill: Placed) =>
  skill.level === "person" ? `${skill.userName ?? "One person"}'s own` : skill.level === "scope" ? scopeLabel(skill) : "The agents'";

/** Who has it in words, for a hover title: a scope's skill reaches every agent under it, the other two the agents they are attached to. */
export const usedByLabel = (skill: Placed, agents: AgentDTO[]) => {
  if (skill.level === "scope") return `every agent under ${scopeLabel(skill)}`;
  if (skill.allAgents) return "every agent";
  if (!skill.grantedAgentIds.length) return "no agent yet";
  const names = skill.grantedAgentIds.map((id) => agents.find((a) => a.id === id)?.name ?? "an agent");
  return names.length > 3 ? `${names.slice(0, 3).join(", ")} +${names.length - 3}` : names.join(", ");
};

/** Each level's icon, the admin sidebar's for the same thing: the tags here and the Skills page's tabs both use it. */
export const LEVEL_ICON: Record<SkillLevel, IconName> = { person: "users", scope: "scopes", agent: "agents" };

/** The level in one word, with its icon, its own quiet colour, and what it means on hover. */
const TAGS: Record<SkillLevel, { label: string; tone: "violet" | "blue" | "teal"; hint: string }> = {
  person: { label: "Person", tone: "violet", hint: "One person's skill: theirs alone, on the agents they share it with" },
  scope: { label: "Scope", tone: "blue", hint: "One scope's skill: every agent working under that scope" },
  agent: { label: "Agent", tone: "teal", hint: "The agents' own skill: whoever the agent works for" },
};

/** The attachments of a person's skill that no longer hold, named, with what to do about them. */
export function InactiveNote({ skill, agents, mine = false }: { skill: Placed; agents: AgentDTO[]; mine?: boolean }) {
  const ids = inactiveAgentIds(skill, agents);
  if (!ids.length) return null;
  const names = ids.map((id) => agents.find((a) => a.id === id)?.name ?? "an agent").join(", ");
  const one = ids.length === 1;
  return (
    <p className="text-xs text-warning">
      Inactive on {names}: {mine ? "you no longer own" : "its person no longer owns"} {one ? "that agent" : "those agents"} and {one ? "it does" : "they do"} not take
      people's skills, so no run of {one ? "it" : "them"} is given this one. Take {one ? "it" : "them"} off the list to tidy up.
    </p>
  );
}

/** Which of the three levels a skill belongs to, as a chip beside its version. */
export function LevelTag({ level }: { level: SkillLevel }) {
  const tag = TAGS[level];
  return (
    <span title={tag.hint}>
      <Badge tone={tag.tone}>
        <span className="inline-flex items-center gap-1">
          <Icon name={LEVEL_ICON[level]} className="size-3" />
          {tag.label}
        </span>
      </Badge>
    </span>
  );
}

/**
 * Agents as small pictures, overlapping like the connector marks on an agent's row, the rest counted and named on
 * hover. An attachment that no longer holds is faded and says why.
 */
function AgentMarks({ ids, agents, inactive = [], mine = false, max = 4 }: { ids: string[]; agents: AgentDTO[]; inactive?: string[]; mine?: boolean; max?: number }) {
  const named = ids.map((id) => agents.find((a) => a.id === id) ?? { id, name: "an agent", avatarUrl: null });
  const rest = named.slice(max);
  return (
    <span className="flex shrink-0 items-center gap-1">
      <span className="flex -space-x-1.5">
        {named.slice(0, max).map((a) => (
          <span
            key={a.id}
            title={inactive.includes(a.id) ? `${a.name} — ${inactiveHint(mine)}` : a.name}
            className={`rounded-full ring-2 ring-surface ${inactive.includes(a.id) ? "opacity-40 grayscale" : ""}`}
          >
            <Avatar name={a.name} src={a.avatarUrl} size="sm" />
          </span>
        ))}
      </span>
      {rest.length > 0 && <span className="text-xs text-muted" title={rest.map((a) => a.name).join(", ")}>+{rest.length}</span>}
    </span>
  );
}

const TEXT = "min-w-0 truncate text-xs text-muted";

/**
 * Who has a skill: the person it belongs to and the agents they share it with, a scope by its type and name, or the
 * agents that have it. `person` is off on My skills, where the person reading it is the one it belongs to.
 */
export function SkillWho({ skill, agents, person = true, mine = false }: { skill: Placed; agents: AgentDTO[]; person?: boolean; mine?: boolean }) {
  if (skill.level === "scope") {
    return (
      <span className="flex min-w-0 items-center gap-1.5" title={`Used by every agent under ${scopeLabel(skill)}`}>
        <span className="grid size-6 shrink-0 place-items-center rounded-full bg-haze-soft text-slate dark:bg-haze/15 dark:text-haze">
          <Icon name="scopes" className="size-3.5" />
        </span>
        <span className={TEXT}>{scopeLabel(skill)}</span>
      </span>
    );
  }
  const marks = skill.allAgents ? (
    <span className={TEXT}>Every agent</span>
  ) : skill.grantedAgentIds.length ? (
    <AgentMarks ids={skill.grantedAgentIds} agents={agents} inactive={inactiveAgentIds(skill, agents)} mine={mine} />
  ) : (
    <span className={TEXT}>No agent yet</span>
  );
  if (skill.level === "agent") {
    return (
      <span className="flex min-w-0 items-center gap-1.5" title={`Used by ${usedByLabel(skill, agents)}`}>
        {marks}
      </span>
    );
  }
  const who = skill.userName ?? "One person";
  return (
    <span className="flex min-w-0 items-center gap-1.5" title={`${who}'s own skill, used by ${usedByLabel(skill, agents)}`}>
      {person && <Avatar name={who} size="sm" />}
      {person && <span className={TEXT}>{who}</span>}
      {marks}
    </span>
  );
}
